import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Add, Delete, Edit, Save, Close } from "@mui/icons-material";
import {
  useAddProductFeatureSuccessCriteriaMutation,
  useDeleteProductFeatureSuccessCriteriaMutation,
  useFetchProductFeatureSuccessCriteria,
  useUpdateProductFeatureSuccessCriteriaMutation,
} from "../../../hooks/product-hooks";
import { ProductFeatureSuccessCriteria } from "../../../types/product";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";

type Props = {
  productId: number;
  featureId?: number;
  readOnly?: boolean;
};

type DraftRow = {
  id?: number;
  success_criteria: string;
  test_plan: string;
};

const emptyDraft: DraftRow = {
  success_criteria: "",
  test_plan: "",
};

export default function ProductFeatureSuccessCriteriaSection({
                                                               productId,
                                                               featureId,
                                                               readOnly = false,
                                                             }: Props) {
  const canManage = !!featureId;

  const {
    data: criteria = [],
    isLoading,
  } = useFetchProductFeatureSuccessCriteria(featureId, {}, { enabled: canManage });

  const { mutateAsync: addCriteria, isPending: adding } =
    useAddProductFeatureSuccessCriteriaMutation(productId, featureId);

  const { mutateAsync: updateCriteria, isPending: updating } =
    useUpdateProductFeatureSuccessCriteriaMutation(productId, featureId);

  const { mutateAsync: deleteCriteria, isPending: deleting } =
    useDeleteProductFeatureSuccessCriteriaMutation(productId, featureId);

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<DraftRow>(emptyDraft);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<DraftRow>(emptyDraft);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState<ProductFeatureSuccessCriteria | null>(null);

  const saving = adding || updating || deleting;

  useEffect(() => {
    if (!canManage) {
      setCreating(false);
      setDraft(emptyDraft);
      setEditingId(null);
      setEditDraft(emptyDraft);
      setConfirmOpen(false);
      setRowPendingDelete(null);
    }
  }, [canManage]);

  const sortedCriteria = useMemo(() => {
    return [...(criteria ?? [])].sort((a, b) => a.id - b.id);
  }, [criteria]);

  const handleStartCreate = () => {
    setCreating(true);
    setDraft(emptyDraft);
  };

  const handleCancelCreate = () => {
    setCreating(false);
    setDraft(emptyDraft);
  };

  const handleCreate = async () => {
    if (!featureId) return;
    if (!draft.success_criteria?.trim()) return;

    await addCriteria({
      product_feature: featureId,
      success_criteria: draft.success_criteria.trim(),
      test_plan: draft.test_plan?.trim() || "",
    } as ProductFeatureSuccessCriteria);

    setCreating(false);
    setDraft(emptyDraft);
  };

  const handleStartEdit = (row: ProductFeatureSuccessCriteria) => {
    setEditingId(row.id);
    setEditDraft({
      id: row.id,
      success_criteria: row.success_criteria ?? "",
      test_plan: row.test_plan ?? "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyDraft);
  };

  const handleSaveEdit = async () => {
    if (!featureId || !editingId) return;
    if (!editDraft.success_criteria?.trim()) return;

    await updateCriteria({
      id: editingId,
      product_feature: featureId,
      success_criteria: editDraft.success_criteria.trim(),
      test_plan: editDraft.test_plan?.trim() || "",
    } as ProductFeatureSuccessCriteria);

    setEditingId(null);
    setEditDraft(emptyDraft);
  };

  const handleRequestDelete = (row: ProductFeatureSuccessCriteria) => {
    setRowPendingDelete(row);
    setConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setRowPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowPendingDelete?.id) return;

    await deleteCriteria({ id: rowPendingDelete.id });

    if (editingId === rowPendingDelete.id) {
      setEditingId(null);
      setEditDraft(emptyDraft);
    }

    setConfirmOpen(false);
    setRowPendingDelete(null);
  };

  return (
    <>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Success Criteria
          </Typography>

          {!readOnly && canManage && !creating && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={handleStartCreate}
              disabled={saving}
            >
              Add Criteria
            </Button>
          )}
        </Box>

        {!canManage ? (
          <Typography variant="body2" color="text.secondary">
            Save the feature first to manage success criteria.
          </Typography>
        ) : (
          <>
            {isLoading && (
              <Box display="flex" alignItems="center" gap={1} py={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading success criteria…</Typography>
              </Box>
            )}

            {!readOnly && creating && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="grid" gap={2}>
                  <TextField
                    label="Success Criteria"
                    value={draft.success_criteria}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, success_criteria: e.target.value }))
                    }
                    fullWidth
                    required
                  />
                  <TextField
                    label="Test Plan"
                    value={draft.test_plan}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, test_plan: e.target.value }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Button onClick={handleCancelCreate} startIcon={<Close />}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCreate}
                      startIcon={<Save />}
                      disabled={!draft.success_criteria.trim() || saving}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}

            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Success Criteria</TableCell>
                    <TableCell>Test Plan</TableCell>
                    {!readOnly && <TableCell width={120}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCriteria.map((row) => {
                    const isEditing = editingId === row.id;

                    return (
                      <TableRow key={row.id}>
                        <TableCell sx={{ verticalAlign: "top", minWidth: 240 }}>
                          {isEditing ? (
                            <TextField
                              value={editDraft.success_criteria}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  success_criteria: e.target.value,
                                }))
                              }
                              fullWidth
                              size="small"
                            />
                          ) : (
                            row.success_criteria || (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )
                          )}
                        </TableCell>

                        <TableCell sx={{ verticalAlign: "top", minWidth: 320 }}>
                          {isEditing ? (
                            <TextField
                              value={editDraft.test_plan}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  test_plan: e.target.value,
                                }))
                              }
                              fullWidth
                              size="small"
                              multiline
                              minRows={2}
                            />
                          ) : (
                            row.test_plan || (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )
                          )}
                        </TableCell>

                        {!readOnly && (
                          <TableCell sx={{ verticalAlign: "top" }}>
                            {isEditing ? (
                              <Box display="flex" gap={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveEdit}
                                  disabled={!editDraft.success_criteria.trim() || saving}
                                >
                                  <Save fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleCancelEdit} disabled={saving}>
                                  <Close fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box display="flex" gap={0.5}>
                                <IconButton size="small" onClick={() => handleStartEdit(row)} disabled={saving}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRequestDelete(row)}
                                  disabled={saving}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}

                  {sortedCriteria.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={readOnly ? 2 : 3}>
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <AdminConfirmDialog
        open={confirmOpen}
        title="Delete Success Criteria"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You are about to permanently delete this product feature success criteria.
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This action cannot be undone.
            </Typography>
            {rowPendingDelete?.success_criteria && (
              <Box mt={2}>
                <Typography variant="subtitle2">Success Criteria</Typography>
                <Typography variant="body2" color="text.secondary">
                  {rowPendingDelete.success_criteria}
                </Typography>
              </Box>
            )}
          </Box>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
}